import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import store from "@/store";
import { useTranslation } from "@/locales";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

/**
 * Logs a message to the console with the specified color.
 *
 * @param {string} colorName - The name of the color to use for the message.
 * @param {string} message - The message to log to the console.
 * @param {any} value - Optional value to log
 */
export const logColor = (colorName, message, value) => {
    const colors = {
        "pink": "#D84999",
        "coral": "#FF8E6E",
        "blue": "#afd",
        "yellow": "#FFC300",
        "magenta": "#FF00CC",
        "lightBlue": "#D4F6FF",
        "orange": "#FF8300",
        "teal": "#00BF8F",
    };

    const color = colors[colorName.toLowerCase()];

    if (color) {
        console.log(`%c ${message}`, `color: ${color}`, value);
    } else {
        console.log('Invalid color name. Please use a valid color name.');
    }
};

/**
 * Extracts IDs from an array of category objects
 * @param {Array<Object>} categories - Array of category objects
 * @returns {Array<string>} Array of category IDs
 */
export const getCategoryIds = (categories) => {
    return categories.map(category => category.id);
};

/**
 * Translates the given weekday identifier into the corresponding localized string.
 *
 * @param {string} day - The identifier of the weekday to translate.
 * @returns {string} The translated weekday string.
 */
export const translatedWeekday = (day) => {
    const { translate } = useTranslation();
    const key = "global.weekdays." + day;
    if (!translate(key)) {
        console.error(`Translation key not found: ${key}`);
    }
    return translate(key);
};

export const showToastCreateRecipient = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.creating.recipient"),
            variant: "default"
        });
    }
};

export const showToastDeleteRecipient = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.deleting.recipient"),
            variant: "default"
        });
    }
};

export const showToastUpdateRecipient = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.recipient"),
            variant: "default"
        });
    }
};

export const showToastFetchRecipients = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.fetching.recipients"),
            variant: "default"
        });
    }
};

/**
 * Toast functions for Profiles
 */
export const showToastCreateProfile = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.creating.profile"),
            variant: "default"
        });
    }
};

export const showToastDeleteProfile = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.deleting.profile"),
            variant: "default"
        });
    }
};

export const showToastUpdateProfile = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.profile"),
            variant: "default"
        });
    }
};

export const showToastFetchProfiles = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.fetching.profiles"),
            variant: "default"
        });
    }
};

export const showToastCreateTimeslot = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.creating.timeslot"),
            variant: "default"
        });
    }
};

export const showToastDeleteTimeslot = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.deleting.timeslot"),
            variant: "default"
        });
    }
};

export const showToastUpdateTimeslot = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.timeslot"),
            variant: "default"
        });
    }
};

export const showToastFetchTimeslots = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.fetching.timeslots"),
            variant: "default"
        });
    }
};

export const showToastProfileValidation = (toast, error = '') => {
    const { translate } = useTranslation();
    toast({
        title: translate("error.title"),
        description: translate(error),
        variant: "destructive"
    });
};

export const showToastProfileAction = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.profile"),
            variant: "default"
        });
    }
};
export const showToastProfileStatus = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.profile"),
            variant: "default"
        });
    }
};

/**
 * Toast functions for Actions
 */
export const showToastAddAction = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.creating.action"),
            variant: "default"
        });
    }
};

export const showToastDeleteAction = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.deleting.action"),
            variant: "default"
        });
    }
};

export const showToastUpdateCallRoutingExceptionTime = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: "Call routing exception time updated successfully",
            variant: "default"
        });
    }
};

/**
 * Get default messages for profile active/inactive states
 */
export const getDefaultProfileMessages = () => {
    const { translate } = useTranslation();

    return {
        active: translate("configuration.callhandling.profile.messages.defaults." + store.devicesModule.getters.getData("PERSONA_ID") + ".active", { email: '${EMAIL_SPEAKABLE}' }),
        inactive: translate("configuration.callhandling.profile.messages.defaults." + store.devicesModule.getters.getData("PERSONA_ID") + ".inactive", { email: '${EMAIL_SPEAKABLE}' })
    };
};

/**
 * Get message labels for profile messages
 */
export const getProfileMessageLabels = () => {
    const { translate } = useTranslation();

    return {
        active: translate("configuration.callhandling.profile.messages.labels.active"),
        inactive: translate("configuration.callhandling.profile.messages.labels.inactive")
    };
};

/**
 * Toast function for Schedule Messages
 */
export const showToastUpdateMessage = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.profile"),
            variant: "default"
        });
    }
};



export const showToastUpdateCallRoutingObject = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.callrouting"),
            variant: "default"
        });
    }
};


export const showToastNewCallRoutingObject = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.creating.callrouting"),
            variant: "default"
        });
    }
};


export const showToastDeleteCallRoutingObject = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.deleting.callrouting"),
            variant: "default"
        });
    }
};


export const showToastUpdateGreetingMessage = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.callroutingGreeting"),
            variant: "default"
        });
    }
};



export const showToastUpdateGoodbyeMessage = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.updating.callroutingGoodbye"),
            variant: "default"
        });
    }
};




export const showToastErrorModifyCallRoutingObject = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    }
};


export const showToastDeleteCallRoutingException = (toast, error = '') => {
    const { translate } = useTranslation();
    if (error) {
        toast({
            title: translate("error.title"),
            description: translate(error),
            variant: "destructive"
        });
    } else {
        toast({
            title: translate("success.title"),
            description: translate("success.deleting.callroutingException"),
            variant: "default"
        });
    }
};



/**
 * Formats a phone number by combining prefix and number, removing leading zeros
 * @param {string} prefix - The country prefix (e.g., '+49')
 * @param {string} number - The phone number (e.g., '017632931548')
 * @returns {string} Formatted phone number (e.g., '+4917632931548')
 */
export const formatPhoneNumber = (prefix, number) => {
    if (!prefix || !number) return '';

    // Remove any spaces, dashes or other non-digit characters except '+'
    const cleanPrefix = prefix.replace(/[^\d+]/g, '');
    const cleanNumber = number.replace(/[^\d]/g, '');

    const formattedNumber = cleanNumber.startsWith('0')
        ? cleanNumber.substring(1)
        : cleanNumber;

    return `${cleanPrefix}${formattedNumber}`;
};


/**
 * Checks if a given string is the "DEVICE_SELECTOR".
 *
 * @param {string} str - The string to check
 * @returns {boolean} True if the string equals "DEVICE_SELECTOR", false otherwise
 */
export function isMdsDevice(str) {
    return str === "DEVICE_SELECTOR";
}



export const getCallRoutingListObject = (deviceConfiguration, index = 0) => {
    if (!deviceConfiguration?.selectionElements?.[index]?.possibleSelections) {
        return {};
    }

    return deviceConfiguration.selectionElements[index].possibleSelections;
};

export const fetchAvailableDevices = () => {
    let devicesList = [];
    const devices = store.devicesModule.getters.getAvailableDevices();
    if (devices && devices.length > 0) {
        devicesList = devices;
    }
    return devicesList;
};


/**
 * Gets customer name for a specific device ID
 * @param {string} deviceId - The ID of the device to look up
 * @returns {Promise<string|null>} The customer name if found, null otherwise
 */
export const getCustomerNameByDeviceId = async (deviceId) => {
    try {
        const devicesList = await fetchAvailableDevices();
        const selectedDevice = devicesList.find(device => device.deviceId === deviceId);
        return selectedDevice ? selectedDevice.customerName : null;
    } catch (error) {
        console.error("Error getting customer name:", error);
        return null;
    }
};

/**
 * Checks if an object is empty.
 *
 * @param {Object} obj - The object to check.
 * @returns {boolean} True if the object is empty, false otherwise.
 */
export const isObjectEmpty = (obj) => Object.keys(obj).length === 0;

/**
 * Updates an entry at a specific index in the entries array.
 *
 * @param {Array} entries - The array of entries.
 * @param {number} index - The index to update.
 * @param {Object} newObject - The new object to insert.
 * @returns {Object} The updated entries as an object.
 */
export const updateEntryAtIndex = (entries, index, newObject) => {
    if (index < 0 || index >= entries.length) {
        console.error("Invalid index:", index);
        return entries;
    }

    entries[index] = [newObject.targetNumber, newObject];

    return Object.fromEntries(entries);
};

/**
 * Deletes an entry at a specific index from the entries array.
 *
 * @param {Array} entries - The array of entries.
 * @param {number} indexToDelete - The index of the entry to delete.
 * @returns {Object|null} The updated entries as an object, or null if the index is invalid.
 */
export const deleteEntryByIndex = (entries, indexToDelete) => {
    if (indexToDelete < -1 || indexToDelete >= entries.length) {
        return null;
    }

    const updatedEntries = [...entries];
    updatedEntries.splice(indexToDelete, 1);
    return Object.fromEntries(updatedEntries);
}



/**
 * Generates a device title based on the device's properties.
 *
 * @param {Object} device - The device object containing details.
 * @returns {string} The formatted device title.
 */
export const getDeviceTitle = (device) => {
    let title = device.customerName + " - ";

    if ("DEVICE_SELECTOR" === device.type) {
        title += "MDS";
    } else {
        title += device.deviceConfiguration["LOCATION_NAME"] + " -";

        if (device.phoneNumbers && device.phoneNumbers.length > 0) {
            title += " " + device.phoneNumbers[0];
        }

        title += " (" + device.deviceConfiguration["LANGUAGE"] + ")";

        if (store.authModule.getters.canSeeDeviceId()) {
            title += " - " + device.deviceId;
        }
    }

    return title;
};

/**
 * Retrieves a device from the list by its ID.
 *
 * @param {Array} devicesList - The list of devices.
 * @param {string} deviceId - The ID of the device to find.
 * @returns {Object|null} The found device object or null if not found.
 */
export const getDeviceById = (devicesList, deviceId) => {
    try {
        const selectedDevice = devicesList.find(device => device.deviceId === deviceId);
        return selectedDevice || null;
    } catch (error) {
        console.error("Error getting device by ID:", error);
        return null;
    }
};

/**
 * Validates the start and end times.
 *
 * @param {Date} startTime - The start time to validate.
 * @param {Date} endTime - The end time to validate.
 * @returns {Object} An object containing isValid status and error message if invalid.
 */
export const validateTimes = (startTime, endTime) => {
    if (!startTime || !endTime) {
        return {
            isValid: false,
            error: 'Both start and end times are required'
        };
    }

    if (endTime <= startTime) {
        return {
            isValid: false,
            error: 'End time must be after start time'
        };
    }

    return {
        isValid: true,
        error: ''
    };
};

/**
 * Checks if the object has valid attributes.
 *
 * @param {Object} obj - The object to check.
 * @returns {boolean} True if the object has valid attributes, false otherwise.
 */
export const hasValidAttributes = (obj) => {
    return obj && typeof obj === 'object' &&
        'validFrom' in obj &&
        'validTo' in obj &&
        'validOnDay' in obj;
};












